import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentIcon from "@mui/icons-material/Assignment";
import GroupsIcon from "@mui/icons-material/Groups";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import StatusChip from "../common/StatusChip";
import EmptyState from "../common/EmptyState";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AttendanceRegularization = () => {
  const role = sessionStorage.getItem("role") || "";
  const token = sessionStorage.getItem("token");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [attendanceDate, setAttendanceDate] = useState("");
  const [requestedCheckInTime, setRequestedCheckInTime] = useState("");
  const [requestedCheckOutTime, setRequestedCheckOutTime] = useState("");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewRemarks, setReviewRemarks] = useState({});
  const [actingId, setActingId] = useState("");

  const endpointForRole = useMemo(() => {
    if (role === "admin") return "/api/attendance-regularization/admin-requests";
    if (role === "manager") return "/api/attendance-regularization/manager-requests";
    return "/api/attendance-regularization/my-requests";
  }, [role]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}${endpointForRole}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Failed to fetch requests");
      setRequests(data.requests || []);
    } catch (err) {
      setError(err.message || "Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchRequests();
  }, [token, endpointForRole]);

  const stats = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((r) => r.status === "pending").length;
    const approved = requests.filter((r) => r.status === "approved").length;
    const rejected = requests.filter((r) => r.status === "rejected").length;
    return { total, pending, approved, rejected };
  }, [requests]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    try {
      setError("");
      setMessage("");
      setIsSubmitting(true);
      if (!requestedCheckInTime && !requestedCheckOutTime) {
        throw new Error("Please provide check-in or check-out time.");
      }

      const body = {
        attendanceDate,
        requestedCheckInTime: requestedCheckInTime || undefined,
        requestedCheckOutTime: requestedCheckOutTime || undefined,
        reason,
      };

      const res = await fetch(`${BASE_URL}/api/attendance-regularization/request`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Request submission failed");

      setMessage(data.msg || "Request submitted");
      setAttendanceDate("");
      setRequestedCheckInTime("");
      setRequestedCheckOutTime("");
      setReason("");
      fetchRequests();
    } catch (err) {
      setError(err.message || "Request submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReview = async (id, action) => {
    try {
      setError("");
      setMessage("");
      setActingId(id);
      const remark = reviewRemarks[id] || "";

      const path =
        role === "admin"
          ? `/api/attendance-regularization/admin-requests/${id}/review`
          : `/api/attendance-regularization/manager-requests/${id}/review`;

      const body =
        role === "admin"
          ? { action, adminRemark: remark }
          : { action, managerRemark: remark };

      const res = await fetch(`${BASE_URL}${path}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg || "Unable to process request");

      setMessage(data.msg || "Request updated");
      fetchRequests();
    } catch (err) {
      setError(err.message || "Unable to process request");
    } finally {
      setActingId("");
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatTime = (d) =>
    new Date(d).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });

  const pageTitle =
    role === "admin"
      ? "Attendance Regularization Control"
      : role === "manager"
      ? "Attendance Regularization Approvals"
      : "Attendance Regularization";

  const pageSubtitle =
    role === "admin"
      ? "Review and act on all regularization requests across teams."
      : role === "manager"
      ? "Approve or reject your team members' attendance corrections."
      : "Raise a correction request for missing or incorrect attendance.";

  return (
    <PageContainer>
      <PageHeader
        gradient
        title={pageTitle}
        subtitle={pageSubtitle}
        icon={<EditCalendarIcon />}
      />

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Total Requests"
            value={stats.total}
            icon={<GroupsIcon />}
            color="#667eea"
            bgColor="#EBF0FF"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Pending"
            value={stats.pending}
            icon={<PendingActionsIcon />}
            color="#92400E"
            bgColor="#FEF3C7"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Approved"
            value={stats.approved}
            icon={<CheckCircleIcon />}
            color="#059669"
            bgColor="#D1FAE5"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Rejected"
            value={stats.rejected}
            icon={<CancelIcon />}
            color="#DC2626"
            bgColor="#FEE2E2"
          />
        </Grid>
      </Grid>

      {/* Request Form (employee only) */}
      {role !== "manager" && role !== "admin" && (
        <Paper
          component="form"
          onSubmit={handleSubmitRequest}
          elevation={0}
          sx={{
            border: "1px solid #eef0f6",
            p: 3,
            borderRadius: "16px",
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#1a1a2e",
              mb: 2,
              pb: 1.5,
              borderBottom: "2px solid #f0f0f5",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <EditCalendarIcon sx={{ fontSize: 20, color: "#667eea" }} />
            Create Regularization Request
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Attendance Date"
                type="date"
                value={attendanceDate}
                onChange={(e) => setAttendanceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Requested Check-in"
                type="time"
                value={requestedCheckInTime}
                onChange={(e) => setRequestedCheckInTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Requested Check-out"
                type="time"
                value={requestedCheckOutTime}
                onChange={(e) => setRequestedCheckOutTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                minRows={2}
                maxRows={3}
                fullWidth
                required
                placeholder="Explain what correction is needed and why."
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" } }}
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            disabled={isSubmitting}
            sx={{
              mt: 2,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              py: 1,
              fontSize: "0.875rem",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
              "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)" },
              "&:disabled": { bgcolor: "#93C5FD", color: "#fff" },
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>
        </Paper>
      )}

      {message && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setMessage("")}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Requests Table */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 3, pb: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1rem",
              color: "#1a1a2e",
              pb: 1.5,
              borderBottom: "2px solid #f0f0f5",
            }}
          >
            {role === "admin"
              ? "All Regularization Requests"
              : role === "manager"
              ? "Manager Queue"
              : "My Regularization Requests"}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} sx={{ color: "#667eea" }} />
          </Box>
        ) : requests.length === 0 ? (
          <EmptyState
            title="No requests found"
            subtitle="There are no regularization requests to display."
          />
        ) : (
          <>
            {/* Desktop Table */}
            {!isMobile ? (
              <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {[
                        "Employee",
                        "Date",
                        "Check-in",
                        "Check-out",
                        "Reason",
                        "Status",
                        ...((role === "manager" || role === "admin") ? ["Action"] : []),
                      ].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            bgcolor: "#f5f7fb",
                            fontWeight: 600,
                            fontSize: "0.8125rem",
                            color: "#5a5f7a",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            py: 1.5,
                            borderBottom: "2px solid #f0f0f5",
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((r) => (
                      <TableRow key={r._id} sx={{ "&:hover": { bgcolor: "#f5f7fb" } }}>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                          {r.employee?.employeeid || r.employeeId}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                          {formatDate(r.attendanceDate)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                          {r.requestedCheckInTime ? formatTime(r.requestedCheckInTime) : "-"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                          {r.requestedCheckOutTime ? formatTime(r.requestedCheckOutTime) : "-"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2, maxWidth: 260 }}>
                          {r.reason}
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <StatusChip status={r.status} />
                        </TableCell>
                        {(role === "manager" || role === "admin") && (
                          <TableCell sx={{ py: 2 }}>
                            {r.status === "pending" ? (
                              <Stack spacing={1} sx={{ minWidth: 180, maxWidth: 220 }}>
                                <TextField
                                  size="small"
                                  placeholder="Remark (optional)"
                                  value={reviewRemarks[r._id] || ""}
                                  onChange={(e) =>
                                    setReviewRemarks((prev) => ({
                                      ...prev,
                                      [r._id]: e.target.value,
                                    }))
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      fontSize: "0.8125rem",
                                    },
                                  }}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    size="small"
                                    disabled={actingId === r._id}
                                    onClick={() => handleReview(r._id, "approve")}
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                      borderRadius: "8px",
                                      fontSize: "0.75rem",
                                      px: 1.5,
                                      py: 0.5,
                                      bgcolor: "#059669",
                                      color: "#fff",
                                      "&:hover": { bgcolor: "#047857" },
                                    }}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={actingId === r._id}
                                    onClick={() => handleReview(r._id, "reject")}
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                      borderRadius: "8px",
                                      fontSize: "0.75rem",
                                      px: 1.5,
                                      py: 0.5,
                                      borderColor: "#DC2626",
                                      color: "#DC2626",
                                      "&:hover": { borderColor: "#B91C1C", bgcolor: "#FEF2F2" },
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </Stack>
                              </Stack>
                            ) : (
                              <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>
                                Reviewed
                              </Typography>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              /* Mobile Cards */
              <Box sx={{ p: 2 }}>
                <Grid container spacing={1.5}>
                  {requests.map((r) => (
                    <Grid item xs={12} key={r._id}>
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid #eef0f6",
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{ px: 2.5, py: 2, bgcolor: "#f5f7fb", borderBottom: "2px solid #f0f0f5" }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}>
                              {r.employee?.employeeid || r.employeeId}
                            </Typography>
                            <StatusChip status={r.status} />
                          </Stack>
                        </Box>
                        <Box sx={{ p: 2.5 }}>
                          <Stack spacing={1}>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Date</Typography>
                              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1a1a2e" }}>
                                {formatDate(r.attendanceDate)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Check-in</Typography>
                              <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                                {r.requestedCheckInTime ? formatTime(r.requestedCheckInTime) : "-"}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Check-out</Typography>
                              <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                                {r.requestedCheckOutTime ? formatTime(r.requestedCheckOutTime) : "-"}
                              </Typography>
                            </Box>
                            <Divider />
                            <Box>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3", mb: 0.5 }}>Reason</Typography>
                              <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e", wordBreak: "break-word" }}>
                                {r.reason}
                              </Typography>
                            </Box>

                            {(role === "manager" || role === "admin") && r.status === "pending" && (
                              <Stack spacing={1}>
                                <TextField
                                  size="small"
                                  placeholder="Remark (optional)"
                                  value={reviewRemarks[r._id] || ""}
                                  onChange={(e) =>
                                    setReviewRemarks((prev) => ({
                                      ...prev,
                                      [r._id]: e.target.value,
                                    }))
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8125rem" },
                                  }}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    fullWidth
                                    size="small"
                                    disabled={actingId === r._id}
                                    onClick={() => handleReview(r._id, "approve")}
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                      borderRadius: "8px",
                                      py: 1,
                                      bgcolor: "#059669",
                                      color: "#fff",
                                      "&:hover": { bgcolor: "#047857" },
                                    }}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    disabled={actingId === r._id}
                                    onClick={() => handleReview(r._id, "reject")}
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                      borderRadius: "8px",
                                      py: 1,
                                      borderColor: "#DC2626",
                                      color: "#DC2626",
                                      "&:hover": { borderColor: "#B91C1C", bgcolor: "#FEF2F2" },
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </Stack>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Paper>
    </PageContainer>
  );
};

export default AttendanceRegularization;
